// Model choice helpers (pure): which model a step uses, what it costs, and how to say it.
// Resolution order (spec-v2.1.md, A): the clip's own choice, then the project's, then the account's, then the default.
import type { ModelInfo, ModelStep } from "./types";

export const STEP_LABEL: Record<ModelStep, string> = { image: "Frames", clip: "Video clips", sfx: "Sound effects", music: "Music", voice: "Voiceover" };
export const MODEL_STEPS: ModelStep[] = ["clip", "image", "voice", "music", "sfx"];

export type ModelChoice = { clip?: string | null; project?: Partial<Record<ModelStep, string>>; account?: Partial<Record<ModelStep, string>>; defaults?: Partial<Record<ModelStep, string>> };

export function resolveModel(step: ModelStep, c: ModelChoice, catalog: ModelInfo[] = []): string | null {
  const fromCatalogDefault = catalog.find((m) => m.step === step && m.isDefault)?.id;
  return (step === "clip" ? c.clip : null) || c.project?.[step] || c.account?.[step] || c.defaults?.[step] || fromCatalogDefault || null;
}

/** Where the current choice comes from, for the picker's note. */
export function choiceSource(step: ModelStep, c: ModelChoice): "scene" | "project" | "account" | "default" {
  if (step === "clip" && c.clip) return "scene";
  if (c.project?.[step]) return "project";
  if (c.account?.[step]) return "account";
  return "default";
}

/** Credits for one item: a clip of `durationS`, or one of anything else. */
export function creditsFor(m: ModelInfo, durationS = 5): number {
  // a clip is billed at the length the model makes: the shortest allowed one that covers the scene (as the api does),
  // so the price on a confirm sheet matches the server's and can be sent as expectCredits
  const allowed = [...(m.durations ?? [])].sort((a, b) => a - b);
  const billed = allowed.length ? (allowed.find((d) => d >= durationS - 0.05) ?? allowed[allowed.length - 1]!) : durationS;
  if (m.creditsPer5s != null) return Math.ceil((m.creditsPer5s * billed) / 5);
  return m.credits ?? 0;
}

/** "25 credits per 5 s", "8 credits each", "15 credits each, 25 with reference pictures". */
export function priceLabel(m: ModelInfo): string {
  if (m.creditsPer5s != null) return `${m.creditsPer5s} credits per 5 s`;
  if (m.credits != null) return `${m.credits} credits each${refsWords(m)}`;
  return "Price on request";
}

/** ", 25 with reference pictures" for a picture model priced by references (GPT Image, FLUX.2 Pro); else "". */
export function refsWords(m: ModelInfo): string {
  return m.creditsWithRefs != null && m.creditsWithRefs !== m.credits ? `, ${m.creditsWithRefs} with reference pictures` : "";
}

/** "+18 credits", "-6 credits", "Same price" for a clip of `durationS` when switching from `from` to `to`. */
export function priceDelta(from: ModelInfo | undefined, to: ModelInfo, durationS = 5): string {
  if (!from) return `${creditsFor(to, durationS)} credits`;
  const d = creditsFor(to, durationS) - creditsFor(from, durationS);
  if (d === 0) return "Same price";
  return `${d > 0 ? "+" : "−"}${Math.abs(d)} credits`;
}

/** A label for a stored model id, including the pre-v2.1 "runway:draft" / "runway:final". */
export function modelLabel(id: string | null | undefined, catalog: ModelInfo[]): string {
  if (!id) return "Default";
  const hit = catalog.find((m) => m.id === id);
  if (hit) return hit.label;
  if (id === "runway:draft") return "Runway (draft)";
  if (id === "runway:final") return "Runway (final)";
  const [provider, name] = id.split(":");
  return name ? `${provider.replace(/^./, (c) => c.toUpperCase())} ${name.replace(/[_-]/g, " ")}` : id;
}

/**
 * Keaton 2026-10-30: a scene's picture and video model, each its own pick or else the video's default for that scene
 * (the server's `defaults`, which knows model rules and the automatic lip-sync model; else project > account >
 * default here). The two never affect each other. `label` reads "Video default (X)" when the scene has no pick.
 */
export type SceneModel = { id: string | null; own: string | null; defaultId: string | null; label: string; why?: "rule" | "lipsync" };
export function sceneModels(
  shot: { clipModel?: string | null; imageModel?: string | null; defaults?: { image: string; clip: string; clipWhy?: "rule" | "lipsync" } | null } | undefined,
  c: Omit<ModelChoice, "clip">,
  catalog: ModelInfo[] = [],
): { image: SceneModel; clip: SceneModel } {
  const one = (step: "image" | "clip"): SceneModel => {
    const own = (step === "clip" ? shot?.clipModel : shot?.imageModel) || null;
    const defaultId = (step === "clip" ? shot?.defaults?.clip : shot?.defaults?.image) || resolveModel(step, { ...c, clip: null }, catalog);
    const why = step === "clip" ? shot?.defaults?.clipWhy : undefined;
    const label = own ? modelLabel(own, catalog) : `Video default (${modelLabel(defaultId, catalog)}${why === "lipsync" ? ", for lip sync" : ""})`;
    return { id: own ?? defaultId, own, defaultId, label, ...(why ? { why } : {}) };
  };
  return { image: one("image"), clip: one("clip") };
}

/** What made a scene's picture and clip: "Picture: GPT Image · Clip: Kling 2.5" (only the parts that exist). */
export function madeWithLine(shot: { frame?: { model?: string | null } | null; clip?: { model?: string | null } | null } | undefined, catalog: ModelInfo[] = []): string {
  const parts: string[] = [];
  if (shot?.frame) parts.push(`Picture: ${shot.frame.model ? modelLabel(shot.frame.model, catalog) : "not recorded"}`);
  if (shot?.clip?.model) parts.push(`Clip: ${modelLabel(shot.clip.model, catalog)}`);
  return parts.join(" · ");
}
